import React from 'react';
import { Tooltip } from 'antd';
import styles from './CandidateSourceAndName.module.scss';
import SourceImageIcon from '../../CandidateListItem/SourceImageIcon/SourceImageIcon';
import {
  getInternalSourceWithCandidateId,
  getAtsSource,
  getSourceDisplayName,
  getPushedAtsSource,
  getCandidateSource,
  getPortalName,
} from '../../../Utils/SourceUtils';
import { isInternalIconVisible } from '../../../Utils/DownloadedCandidatesSourceUtils';
import CandidateViewIconV2 from '../../../Icons/CandidateViewIconV2';
import ChatIconV2 from '../../../Icons/ChatIconV2';
import eventTypes from '../../../Analytics/EventTypes';
import { getEventNameByCandidateStatus } from '../../../Analytics/Candidate/CandidateEventUtils';
import CandidateCardShowDuplicate from '../CandidateCardShowDuplicate/CandidateCardShowDuplicate';
import { getIsUserProhibitedFromPerformingActions } from '../../../Utils/CandidatePublishUtils';
import config from '../../../Config/Config';

function CandidateSourceAndName(props) {
  const {
    candidate = {},
    userConfig = {},
    version,
    whiteLabelInfo,
    featureToggleList,
    isCandidateViewIconVisible,
    openCandidateView,
    isCandidateGroupHead,
    duplicateCandidateIds,
    adminFeatures,
    msp,
    candidateContext,
  } = props;

  const { Name: candidateName, Sources: candidateSources } = candidate;
  const { RevealPortalsUnderGroup: revealPortalsUnderGroup, WhiteGloveServiceProgress: whiteGloveServiceProgress } =
    featureToggleList;
  const {
    MaskActiveAsInternal: { IsEnabled: isMaskActiveAsInternalEnabled },
    ConnectV2: { IsEnabled: isConnectV2Enabled },
  } = featureToggleList;

  const isUserProhibitedFromPerformingActions = getIsUserProhibitedFromPerformingActions(
    adminFeatures,
    featureToggleList,
    candidateContext
  );
  const isCandidateChatIconVisible =
    isConnectV2Enabled &&
    candidate.ConversationId &&
    candidate.RecentConversationTime &&
    !isUserProhibitedFromPerformingActions;

  const { ShowVaultName: shouldShowVaultName } = userConfig;
  const showVaultName = shouldShowVaultName ?? false;
  const revealActiveChannelSourceName = revealPortalsUnderGroup.IsEnabled;
  const isSRPulseUser = whiteGloveServiceProgress?.IsEnabled;

  const isAtsVersion = version === 'ats';

  const atsSource = getAtsSource(candidateSources);
  const candidateSource = isAtsVersion
    ? getInternalSourceWithCandidateId(candidateSources)
    : getPushedAtsSource(candidateSources);

  const sourceDisplayName = getSourceDisplayName(
    atsSource || getCandidateSource(candidate),
    userConfig,
    showVaultName,
    {},
    whiteLabelInfo,
    revealActiveChannelSourceName
  );

  const candidatePortalName = getPortalName(candidateSources, revealActiveChannelSourceName);

  const candidateSourcePortal =
    candidatePortalName?.toLowerCase() === 'internal' && isAtsVersion
      ? getSourceDisplayName(
          candidateSource,
          userConfig,
          showVaultName,
          {},
          whiteLabelInfo,
          revealActiveChannelSourceName
        )
      : sourceDisplayName;

  const showInternalIcon = isInternalIconVisible(candidate, isSRPulseUser);
  const sourceIconStyle = { fontSize: 18, display: 'flex' };

  const showPortalIcons = (
    <div className={styles.overlappingSourceIcons}>
      <div className={styles.sourceImageContainer}>
        <SourceImageIcon
          tooltipVisibility
          placement="top"
          sourceName={candidateSourcePortal}
          sourcePortal={candidatePortalName}
          iconStyle={sourceIconStyle}
          showVaultName={showVaultName}
          isMaskActiveAsInternalEnabled={isMaskActiveAsInternalEnabled}
        />
      </div>
      {showInternalIcon && !isMaskActiveAsInternalEnabled && (
        <div className={styles.overlappingSourceImage}>
          <SourceImageIcon
            tooltipVisibility
            placement="top"
            sourceName="Internal"
            sourcePortal="InternalForDownloadedCandidates"
            iconStyle={sourceIconStyle}
            showVaultName={showVaultName}
          />
        </div>
      )}
    </div>
  );

  const handleCandidateView = e => {
    e.stopPropagation();
    openCandidateView(candidate);
  };

  const candidateViewIcon =
    isCandidateViewIconVisible && !isUserProhibitedFromPerformingActions ? (
      <CandidateViewIconV2
        onClick={handleCandidateView}
        sk-event-name={getEventNameByCandidateStatus(eventTypes.candidate.candidateView.viewInNewTab, candidate)}
      />
    ) : null;

  const handleChatView = e => {
    e.stopPropagation();
    const chatUrl = `${config.urls.aryaConnectV2Chat}/${candidate.ConversationId}`;
    window.open(chatUrl, '_blank', 'noopener,noreferrer');
  };

  const candidateChatIcon =
    candidate.ConversationId && !isUserProhibitedFromPerformingActions ? (
      <Tooltip title="Smart Connect">
        <ChatIconV2 onClick={handleChatView} style={{ cursor: 'pointer' }} />
      </Tooltip>
    ) : null;

  const candidateShowDuplicateOption =
    isCandidateGroupHead && duplicateCandidateIds.length ? <CandidateCardShowDuplicate /> : null;

  return (
    <div className={styles.candidateSourceAndNameWrapper}>
      <div className={styles.candidateSourceAndName}>
        {!msp ? (
          showPortalIcons
        ) : (
          <SourceImageIcon
            tooltipVisibility
            placement="top"
            sourceName="Internal"
            sourcePortal="InternalForDownloadedCandidates"
            iconStyle={sourceIconStyle}
            showVaultName={showVaultName}
            isMaskActiveAsInternalEnabled={isMaskActiveAsInternalEnabled}
          />
        )}
        <Tooltip title={candidateName}>
          <span>{candidateName}</span>
        </Tooltip>
      </div>
      {candidateShowDuplicateOption}
      {!msp && candidateContext !== 'candidateCreation' && isCandidateChatIconVisible ? candidateChatIcon : ''}
      {!msp && candidateContext !== 'candidateCreation' ? candidateViewIcon : ''}
    </div>
  );
}

export default CandidateSourceAndName;
